import { APP_STYLE_CONFIG, BUTTON_LABELS } from '@/constants';
import { Button, SimulationProgressNavProps } from '@/components';
import { cn } from '@/utils';
import { SimulationProgressNavStepIcon, SimulationProgressNavStepLabel } from './subcomponents';
import React, { useMemo, useState } from 'react';

/**
 * SimulationProgressNav component that displays the current progress through
 * simulation steps with a Next button to advance to the next step.
 */
export const SimulationProgressNav: React.FC<SimulationProgressNavProps> = ({
  activeStep,
  animatingStepId = null,
  className,
  disableNext = false,
  disablePrevious = false,
  iconAnimationType = APP_STYLE_CONFIG.useAnimatedPrepProgress ? 'pop' : 'none',
  onComplete,
  onNext,
  onPrevious,
  renderPreviousAsButton = false,
  steps = [],
}) => {
  // ===============================
  // Memoized values
  // ===============================
  const [isPending, setIsPending] = useState(false);

  /**
   * Determine if the current step is the last step to conditionally
   * change the button label and behavior.
   */
  const isLastStep = useMemo(() => activeStep === steps.length, [activeStep, steps.length]);

  // ===============================
  // Handlers
  // ===============================
  const handleGetStarted = () => {
    setIsPending(true);
    onComplete?.();
  };


  // ================================
  // Render component
  // ================================
  return (
    <div className={cn('ml-10 flex items-center gap-10', className)}>
      {renderPreviousAsButton && (
        <Button
          disabled={disablePrevious || activeStep === 1}
          iconLeft="ArrowLeft"
          label={BUTTON_LABELS.PREVIOUS}
          onClick={() => onPrevious?.()}
          size="small"
          variant="tertiary"
        />
      )}

      {/* Steps container */}
      <div className="flex items-center gap-2">
        {steps.map((step, index) => (
          // Individual step
          <div
            key={step.id}
            onClick={() => step.status === 'completed' && onPrevious?.(step.id)}
            className={cn('flex items-center gap-1', {
              'cursor-pointer': step.status === 'completed',
            })}
          >
            {/* Step icon */}
            <SimulationProgressNavStepIcon
              animationType={iconAnimationType}
              className="shrink-0"
              shouldAnimateCompleted={step.status === 'completed' && step.id === animatingStepId}
              status={step.status}
              step={step.id}
            />

            {/* Step label */}
            <SimulationProgressNavStepLabel
              index={index}
              label={step.label}
              showSeparator={index < steps.length - 1}
              status={step.status}
            />
          </div>
        ))}
      </div>

      {/* Buttons */}
      <div className="flex min-w-[140px] items-center justify-start gap-4">
        <Button
          disabled={disableNext}
          className={cn('animate-fade-in animate-duration-300 animate-iteration-count-once', {
            'pointer-events-none hidden opacity-0': isLastStep,
          })}
          iconRight="ArrowRight"
          label={BUTTON_LABELS.NEXT}
          onClick={onNext}
          size="small"
          variant="tertiary"
        />
        <Button
          disabled={disableNext}
          className={cn('animate-fade-in animate-duration-300 animate-iteration-count-once', {
            'pointer-events-none hidden opacity-0': !isLastStep,
          })}
          iconRight="ArrowRight"
          label={BUTTON_LABELS.GET_STARTED}
          isTransitioning={isPending}
          onClick={handleGetStarted}
          size="small"
          variant="secondary"
        />
      </div>
    </div>
  );
};

SimulationProgressNav.displayName = 'SimulationProgressNav';

export default SimulationProgressNav;
